import React from 'react';
import { DateRangePicker, LocalizationProvider } from '@mui/lab';
import { Box, TextField, IconButton, Tooltip, Select, MenuItem, FormControl, InputLabel, OutlinedInput, useTheme, useMediaQuery } from '@mui/material';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import moment from 'moment';
import { addDaysToDate, formatDateToOnlyDate, getDateDifferenceInMS, getDayStart, subtractDaysFromDate } from '../utils/common';

interface Props {
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  onChange: (startDate: any, endDate: any) => any,
  allowFutureDates?: boolean,
  id?: string
}

const DateRangeSelector: React.FC<Props> = ({
  startDate,
  endDate,
  allowFutureDates = false,
  id = '',
  onChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const start = startDate ? Date.parse(startDate) : null;
  const end = endDate ? Date.parse(endDate) : null;
  const today = new Date().toISOString();

  const handleQuickSelect = (event: any) => {
    const { value } = event.target;
    const now = moment();
    const yesterday = moment().subtract(1, 'days');

    switch (value) {
      case 'today':
        onChange(
          now.startOf('day').toISOString(),
          now.endOf('day').toISOString()
        );
        break;
      case 'yesterday':
        onChange(
          yesterday.startOf('day').toISOString(),
          yesterday.endOf('day').toISOString()
        );
        break;
      case 'last7':
        onChange(
          now.subtract(6, 'days').startOf('day').toISOString(),
          moment().endOf('day').toISOString()
        );
        break;
      default:
        break;
    }
  };

  const handleDateChange = (range: any) => {
    const [selectedStart, selectedEnd] = range;

    if (selectedStart) {
      const newStartDate = moment(selectedStart).startOf('day');

      let newEndDate;

      if (selectedEnd && moment(selectedEnd).isAfter(newStartDate, 'day')) {
        // Case 1: user actively picked an end date AFTER start date
        newEndDate = allowFutureDates
          ? moment(selectedEnd).endOf('day')
          : moment.min(moment(selectedEnd).endOf('day'), moment().endOf('day'));
      } else {
        // Case 2: no end picked OR start changed → reset to +7 days
        newEndDate = allowFutureDates
          ? moment(newStartDate).add(7, 'days').endOf('day')
          : moment.min(moment(newStartDate).add(7, 'days').endOf('day'), moment().endOf('day'));
      }

      onChange(newStartDate.toISOString(), newEndDate.toISOString());
    } else {
      onChange(null, null); // Reset if startDate is cleared
    }
  };

  const handlePreviousWeekClick = () => {
    const newStartDate = moment(startDate).subtract(1, 'days').startOf('day')
      .toISOString();
    const newEndDate = moment(newStartDate).add(1, 'days').endOf('day').toISOString();

    onChange(newStartDate, newEndDate);
  };

  const handleNextWeekClick = () => {
    const newStartDate = moment(startDate).add(1, 'days').startOf('day')
      .toISOString();
    const newEndDate = moment(newStartDate).add(1, 'days').endOf('day').toISOString();

    onChange(newStartDate, newEndDate);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        border: '1px solid #e0e0e0',
        width: isMobile ? '100%' : 'auto',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9',
        padding: isMobile ? 1 : 2,
        gap: isMobile ? 1 : 0,
      }}
    >
      <Box sx={{ mr: isMobile ? 0 : 2, mb: isMobile ? 1 : 0 }}>
        <FormControl
          size={isMobile ? 'small' : 'small'}
          sx={{
            minWidth: isMobile ? '100%' : 150,
            maxWidth: isMobile ? '100%' : 150
          }}
        >
          <InputLabel id="quick-range-label">Date Range</InputLabel>
          <Select
            labelId="quick-range-label"
            id={`${id}-quick-select`}
            onChange={handleQuickSelect}
            defaultValue="custom"
            sx={{ backgroundColor: '#fff' }}
            input={<OutlinedInput label="Quick Range" />}
          >
            <MenuItem value="custom">Custom</MenuItem>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="yesterday">Yesterday</MenuItem>
            <MenuItem value="last7">Last 7 Days</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
          width: isMobile ? '100%' : 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'space-between' : 'flex-start',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Tooltip title="Previous week">
              <IconButton
                id={id === 'shifts' ? 'Linfox-PreviousWeek' : id}
                color="primary"
                onClick={handlePreviousWeekClick}
                size={isMobile ? 'small' : 'medium'}
              >
                <ChevronLeft fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
            </Tooltip>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 0.5 : 1,
              }}
            >
              <DateRangePicker
                startText="From"
                endText="To"
                value={[start, end]}
                onChange={handleDateChange}
                inputFormat="dd-MM-yyyy"
                renderInput={(startProps, endProps) => (
                  <>
                    <TextField
                      sx={{
                        width: isMobile ? '100px' : '6rem',
                        '& .MuiInput-input': {
                          fontSize: isMobile ? '0.75rem' : '1rem',
                          padding: isMobile ? '4px 0' : '8px 0',
                        }
                      }}
                      id={id === 'shifts' ? 'Linfox-StartDate' : id}
                      {...startProps}
                      variant="standard"
                      color="secondary"
                      size={isMobile ? 'small' : 'medium'}
                    />
                    <Box sx={{ mx: isMobile ? 0.5 : 1 }} />
                    <TextField
                      sx={{
                        width: isMobile ? '100px' : '6rem',
                        '& .MuiInput-input': {
                          fontSize: isMobile ? '0.75rem' : '1rem',
                          padding: isMobile ? '4px 0' : '8px 0',
                        }
                      }}
                      id={id === 'shifts' ? 'Linfox-EndDate' : id}
                      {...endProps}
                      variant="standard"
                      color="secondary"
                      size={isMobile ? 'small' : 'medium'}
                    />
                  </>
                )}
                // maxDate={!allowFutureDates ? new Date() : undefined}
                calendars={1}
              />
            </Box>

            <Tooltip title="Next week">
              <IconButton
                id={id === 'shifts' ? 'Linfox-NextWeek' : id}
                color="primary"
                onClick={handleNextWeekClick}
                size={isMobile ? 'small' : 'medium'}
              >
                <ChevronRight fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
            </Tooltip>
          </LocalizationProvider>
        </Box>
      </Box>
    </Box>
  );
};

export default DateRangeSelector;
